/**
 * Wave 3 SEO cluster (companies): what file to send — rewrite the PDF you have.
 * Not affiliated with Mercadona, Inditex, Amazon, or SEPE. Merged from posts.js.
 */

export default [
  {
    slug: 'cv-para-mercadona',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-hosteleria', 'cv-para-portales-empleo', 'enviar-cv-por-email'],
    es: {
      title: 'CV para Mercadona: qué archivo enviar (tienda, logística, oficinas)',
      description:
        'ClarCV no es Mercadona. Qué PDF suele funcionar en un portal de supermercado: turnos, una página, sin Canva. Reescribe el que ya tienes.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Quien busca “CV para Mercadona” suele tener un Canva o un Word de la ETT y quiere saber qué adjuntar en el portal de empleo de la cadena. ClarCV no es Mercadona, no gestiona sus vacantes y no conoce el interior de su ATS. Esta guía habla del archivo: una página clara, disponibilidad en texto, y un PDF que se abre en el móvil. El proceso concreto (web de empleo, ETT, tienda) lo marca cada oferta; lee el anuncio.',
        },
        {
          type: 'p',
          text: 'El patrón es el de retail y gran consumo: turnos, caja o almacén, a veces carné. Hermanas: [CV para hostelería](/blog/cv-para-hosteleria) (turnos e idiomas) y [CV para portales](/blog/cv-para-portales-empleo). ClarCV (clarcv.com) regenera el PDF que ya tienes en plantilla parseable. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'No inventes un puesto interno ni copies el eslogan de la empresa. Pon el trabajo que hiciste: reposición, caja, mozo, limpieza, administrativo de tienda. El reclutador de tienda no necesita un Europass de tres páginas.',
        },
        { type: 'h2', text: 'Tienda vs logística vs oficinas' },
        {
          type: 'p',
          text: 'Tienda: resumen de 3 líneas con puesto (cajero/a, reposición, charcutería), turnos (rotativos, fines de semana), localidad. Viñetas con volumen si lo tienes (“caja en hora punta”, “tienda de X m²”) sin cifras inventadas. Logística: almacén, picking, carretilla si tienes carné vigente, turnos de noche. Oficinas / IT / compras: deja de usar el CV de tienda; keywords de [CV comercial](/blog/cv-para-comercial) o [tech](/blog/cv-para-tecnologia) según el anuncio.',
        },
        {
          type: 'ul',
          items: [
            'Una página para tienda y almacén. Foto solo si el mercado local la espera; no un selfie.',
            'Disponibilidad y pueblo/ciudad en texto, no “se facilitará”.',
            'Herramientas: TPV, pistola, Excel — si las usaste. No “SAP” por ver una pantalla.',
            'DNI y estado civil fuera del PDF. [Qué no poner](/blog/que-no-poner-en-el-cv).',
            'Nombre de archivo: Nombre_Apellido_Puesto.pdf, no CV_Mercadona_final(3).pdf.',
          ],
        },
        { type: 'h2', text: 'Lo que rompe el portal' },
        {
          type: 'p',
          text: 'PDF-imagen, Canva de dos columnas, 8 MB de foto. El formulario de una cadena grande extrae mal el email si está en un icono. Prueba del bloc de notas: [iconos en el CV](/blog/iconos-en-el-cv). Si aplicas por email a una tienda concreta, asunto con puesto y pueblo: [enviar CV por email](/blog/enviar-cv-por-email).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El portal de supermercado no lee un Canva de 8 MB',
          lead: 'Sube el CV que ya tienes. Una página, turnos en texto, PDF seleccionable. ClarCV no presenta tu candidatura a Mercadona.',
          from: 'Canva de tienda',
          to: 'Una página que se abre',
          button: 'Preparar el archivo',
        },
        { type: 'h2', text: 'Cómo reescribir el Word de la ETT' },
        {
          type: 'p',
          text: 'Agrupa extras de un mes. Quita “orientado al cliente” vacío. Deja turnos y un hecho. En ClarCV subes el archivo, ves estructura y claridad (no un score mágico del ATS de la cadena) y descargas plantilla A4. Tú validas fechas; no inventamos un contrato en Mercadona.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿ClarCV tiene convenio con Mercadona?',
              a: 'No. No somos su portal de empleo ni hablamos en su nombre.',
            },
            {
              q: '¿Pongo “disponibilidad inmediata”?',
              a: 'Si es verdad, en el resumen. No sustituye un puesto y una localidad.',
            },
            {
              q: '¿El mismo CV para todas las tiendas?',
              a: 'La plantilla sí; la ciudad y el turno, ajústalos al anuncio.',
            },
            {
              q: '¿Hace falta Europass?',
              a: 'Casi nunca para tienda. Un PDF corto parseable suele bastar. El anuncio manda.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for Mercadona: which file to send (store, logistics, offices)',
      description:
        'ClarCV is not Mercadona. What PDF usually works on a supermarket portal: shifts, one page, no Canva. Rewrite the one you already have.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'People searching “CV for Mercadona” often have Canva or an agency Word and want to know what to attach on the chain’s careers site. ClarCV is not Mercadona, does not run their vacancies, and does not know the inside of their ATS. This guide is about the file: one clear page, availability as text, and a PDF that opens on a phone. The actual process (careers site, agency, store) is in each ad; read it.',
        },
        {
          type: 'p',
          text: 'The pattern is retail and grocery: shifts, till or warehouse, sometimes a licence. Siblings: [hospitality CV](/blog/cv-para-hosteleria) (shifts and languages) and [CVs for job boards](/blog/cv-para-portales-empleo). ClarCV (clarcv.com) regenerates the PDF you already have into a parseable template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'Do not invent an internal title or copy the company’s slogan. Put the work you did: replenishment, till, warehouse operative, cleaning, store admin. A store recruiter does not need a three-page Europass.',
        },
        { type: 'h2', text: 'Store vs logistics vs offices' },
        {
          type: 'p',
          text: 'Store: 3-line summary with role (cashier, replenishment, deli), shifts (rotating, weekends), town. Bullets with volume if you have it (“peak-hour till”, “store of X m²”) without invented figures. Logistics: warehouse, picking, forklift if the licence is valid, night shifts. Offices / IT / buying: stop using the store CV; keywords from [sales](/blog/cv-para-comercial) or [tech](/blog/cv-para-tecnologia) as the ad says.',
        },
        {
          type: 'ul',
          items: [
            'One page for store and warehouse. Photo only if the local market expects it; not a selfie.',
            'Availability and town as text, not “to be provided”.',
            'Tools: POS, scanner, Excel — if you used them. Not “SAP” for glancing at a screen.',
            'National ID and marital status off the PDF. [What not to put](/blog/que-no-poner-en-el-cv).',
            'Filename: Nombre_Apellido_Puesto.pdf, not CV_Mercadona_final(3).pdf.',
          ],
        },
        { type: 'h2', text: 'What breaks the portal' },
        {
          type: 'p',
          text: 'Image-PDF, two-column Canva, an 8 MB photo. A large chain’s form extracts email badly if it lives in an icon. Notepad test: [icons on the CV](/blog/iconos-en-el-cv). If you email a specific store, subject with role and town: [emailing your CV](/blog/enviar-cv-por-email).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A supermarket portal does not read an 8 MB Canva file',
          lead: 'Upload the CV you already have. One page, shifts as text, selectable PDF. ClarCV does not submit your application to Mercadona.',
          from: 'Store Canva',
          to: 'One page that opens',
          button: 'Prepare the file',
        },
        { type: 'h2', text: 'How to rewrite the agency Word file' },
        {
          type: 'p',
          text: 'Group one-month extras. Cut empty “customer-oriented”. Leave shifts and one fact. In ClarCV you upload the file, see structure and clarity (not a magic score against the chain’s ATS) and download an A4 template. You validate dates; we do not invent a Mercadona contract.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does ClarCV have a deal with Mercadona?',
              a: 'No. We are not their careers site and we do not speak for them.',
            },
            {
              q: 'Should I put “immediate availability”?',
              a: 'If it is true, in the summary. It does not replace a role and a town.',
            },
            {
              q: 'Same CV for every store?',
              a: 'The template yes; town and shift, match the ad.',
            },
            {
              q: 'Do I need Europass?',
              a: 'Almost never for a store. A short parseable PDF usually suffices. The ad wins.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-inditex',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-comercial', 'cv-para-portales-empleo', 'foto-en-el-cv'],
    es: {
      title: 'CV para Inditex: tienda, central y el PDF del portal',
      description:
        'ClarCV no es Inditex ni Zara. Tienda vs oficinas: qué archivo suele pedir un portal de moda y cómo reescribir el que ya tienes.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“CV para Inditex” mezcla dependiente de Zara, almacén, y perfiles de central (producto, IT, finanzas). ClarCV no es Inditex, Zara, Massimo Dutti ni Pull&Bear. No publicamos sus ofertas ni conocemos su software de selección por dentro. Lo que sí se repite en moda retail: un PDF corto para tienda, idiomas si hay turismo, y un archivo que el portal no destroza. El anuncio (web de empleo del grupo, InfoJobs, LinkedIn) manda.',
        },
        {
          type: 'p',
          text: 'Tienda se parece a [hostelería](/blog/cv-para-hosteleria) en turnos y a [comercial](/blog/cv-para-comercial) si hay objetivos de venta. Oficinas: [tech](/blog/cv-para-tecnologia) o el oficio real. Foto: en retail España a menudo se espera; [guía de foto](/blog/foto-en-el-cv). ClarCV regenera el PDF que ya tienes. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Dependiente: venta, talla y turnos — no el lookbook' },
        {
          type: 'p',
          text: 'Antes: “Apasionada de la moda y el trabajo en equipo.” Después: “Dependiente/a (moda, 2022–2025): caja y reposición, objetivos de ticket medio, inglés B1 en temporada, turnos rotativos.” Un Canva con moodboard no es un CV. El ATS no indexa el collage. Visual merchandising: una viñeta con hecho, no un portfolio entero en el PDF del portal; el portfolio va en URL.',
        },
        {
          type: 'ul',
          items: [
            'Marca y ciudad del local si no hay NDA; si no, “cadena moda, centro comercial X”.',
            'Idiomas MCER: costa y Madrid/Barcelona pesan.',
            'Disponibilidad sábados y rebajas en el resumen si es verdad.',
            'Central (HQ): deja el CV de tienda. Usa stack y logros del puesto de oficina.',
            'No copies el manifiesto de la marca. No ayuda al parser.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El portal de moda no parsea un moodboard',
          lead: 'Sube el CV que ya tienes. Una o dos páginas, idiomas y turnos en texto. ClarCV no envía tu candidatura a Inditex.',
          from: 'Canva lookbook',
          to: 'PDF de tienda parseable',
          button: 'Rehacer el archivo',
        },
        { type: 'h2', text: 'HQ, IT y producto' },
        {
          type: 'p',
          text: 'Si la vacante es en Arteixo, Madrid o un hub, el CV es el de esa profesión. Keywords del anuncio, PDF de una columna, inglés si el puesto es bilingüe. [Adaptar a la oferta](/blog/adaptar-cv-a-una-oferta). No uses el mismo PDF de dependiente para un rol de data o de compras.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿ClarCV coloca en Zara?',
              a: 'No. No somos el grupo Inditex ni su portal.',
            },
            {
              q: '¿Pongo todas las marcas del grupo donde eché extras?',
              a: 'Agrupa. Destaca el local de más volumen o más idioma.',
            },
            {
              q: '¿CV en inglés?',
              a: 'Si el anuncio está en inglés, sí. Si no, español con MCER. Guía EN/ES.',
            },
            {
              q: '¿Europass?',
              a: 'Raro para tienda. El anuncio manda.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for Inditex: store, HQ, and the portal PDF',
      description:
        'ClarCV is not Inditex or Zara. Store vs offices: what file a fashion portal usually wants and how to rewrite the one you already have.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“CV for Inditex” mixes a Zara shop floor role, warehouse, and HQ profiles (product, IT, finance). ClarCV is not Inditex, Zara, Massimo Dutti, or Pull&Bear. We do not post their jobs or know their ATS from the inside. What repeats in fashion retail: a short PDF for store roles, languages if there is tourism, and a file the portal does not wreck. The ad (group careers site, InfoJobs, LinkedIn) wins.',
        },
        {
          type: 'p',
          text: 'Store work is close to [hospitality](/blog/cv-para-hosteleria) on shifts and to [sales](/blog/cv-para-comercial) if there are sales targets. Offices: [tech](/blog/cv-para-tecnologia) or the real job family. Photo: often expected in Spanish retail; [photo guide](/blog/foto-en-el-cv). ClarCV regenerates the PDF you already have. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Shop floor: sales, size, shifts — not a lookbook' },
        {
          type: 'p',
          text: 'Before: “Passionate about fashion and teamwork.” After: “Sales assistant (fashion, 2022–2025): till and replenishment, average-basket targets, English B1 in season, rotating shifts.” A Canva moodboard is not a CV. ATS does not index the collage. Visual merchandising: one factual bullet, not a whole portfolio on the portal PDF; the portfolio goes as a URL.',
        },
        {
          type: 'ul',
          items: [
            'Brand and city of the store if there is no NDA; if not, “fashion chain, shopping centre X”.',
            'CEFR languages: coast and Madrid/Barcelona weigh.',
            'Saturday and sale availability in the summary if true.',
            'HQ: drop the store CV. Use the office role’s stack and achievements.',
            'Do not copy the brand manifesto. It does not help the parser.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A fashion portal does not parse a moodboard',
          lead: 'Upload the CV you already have. One or two pages, languages and shifts as text. ClarCV does not send your application to Inditex.',
          from: 'Lookbook Canva',
          to: 'A parseable store PDF',
          button: 'Rebuild the file',
        },
        { type: 'h2', text: 'HQ, IT, and product' },
        {
          type: 'p',
          text: 'If the vacancy is in Arteixo, Madrid, or a hub, the CV is that profession’s CV. Keywords from the ad, one-column PDF, English if the role is bilingual. [Tailor to the job](/blog/adaptar-cv-a-una-oferta). Do not use the shop-assistant PDF for data or buying.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does ClarCV place people at Zara?',
              a: 'No. We are not the Inditex group or their portal.',
            },
            {
              q: 'Should I list every group brand I extraed in?',
              a: 'Group them. Highlight the site with more volume or more language.',
            },
            {
              q: 'CV in English?',
              a: 'If the ad is in English, yes. If not, Spanish with CEFR. EN/ES guide.',
            },
            {
              q: 'Europass?',
              a: 'Rare for a store. The ad wins.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-amazon',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-tecnologia', 'cv-para-portales-empleo', 'cv-ingles-o-espanol'],
    es: {
      title: 'CV para Amazon: el formulario se come el diseño',
      description:
        'ClarCV no es Amazon. Almacén, corporate o tech: un PDF parseable porque el ATS no lee Canva. Reescribe el archivo que ya tienes.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Amazon usa procesos muy automatizados. Un PDF creativo suele morir en el parseo o en un formulario que te pide pegar el CV a trozos. ClarCV no es Amazon, AWS ni Amazon Logistics. No tenemos acceso a Jobscan contra su ATS ni a “el truco de 2026”. Lo que sí funciona en la práctica: texto seleccionable, encabezados estándar, keywords del anuncio que puedas defender, y a menudo una versión en inglés si la vacante está en inglés.',
        },
        {
          type: 'p',
          text: 'Tech y corporate: [CV para tecnología](/blog/cv-para-tecnologia) y [data analyst](/blog/cv-para-data-analyst). Almacén / última milla: turnos y seguridad, más cerca de [hostelería](/blog/cv-para-hosteleria) en formato (una página, disponibilidad). Idioma: [inglés o español](/blog/cv-ingles-o-espanol). ClarCV: subes el PDF, estructura, plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Lo que el formulario suele romper' },
        {
          type: 'p',
          text: 'Columnas, text boxes, iconos de teléfono, barras de skills, foto pesada. Pegar en el bloc de notas tiene que devolver email y fechas. Guía: [Canva y ATS](/blog/cv-canva-y-ats). No pongas el logo de Amazon en la cabecera: no eres empleado hasta que te contraten y parece spam.',
        },
        {
          type: 'ul',
          items: [
            'Título alineado al anuncio (Warehouse Associate, SDM, SDE) sin inflar el nivel.',
            'Principios de liderazgo: no copies los 16 en el CV. Un logro que se pueda preguntar vale más que la jerga.',
            'Métricas de almacén o de producto solo si son tuyas y no confidenciales.',
            'Una o dos páginas. El historial de 15 años se compacta: [CV +50](/blog/cv-mayores-de-50).',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Workday no lee tu infográfico',
          lead: 'Da igual el empleador: si el PDF no se pega en orden, el formulario fallará. Regenera una columna de texto. ClarCV no postula por ti en Amazon.',
          from: 'CV creativo',
          to: 'PDF que el ATS extrae',
          button: 'Arreglar el parseo',
        },
        { type: 'h2', text: 'Almacén vs corporate vs tech' },
        {
          type: 'p',
          text: 'Almacén: turnos, carretilla, seguridad, localidad del centro. Corporate España: el oficio (finanzas, HR, vendor). Tech: stack honesto, no “Amazon intern” inventado. Adapta 20 minutos al anuncio. No uses el mismo PDF para picker y para SDE.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿ClarCV garantiza pasar el ATS de Amazon?',
              a: 'No. Nadie serio puede garantizarlo. Mejoramos claridad y formato parseable.',
            },
            {
              q: '¿Debo escribir el CV en inglés?',
              a: 'Si la oferta está en inglés, sí. Si está en español para un centro logístico local, español con keywords del anuncio.',
            },
            {
              q: '¿Pongo “customer obsessed”?',
              a: 'Mejor un hecho. La jerga copiada de la web corporativa no sustituye un logro.',
            },
            {
              q: '¿Sois el portal de Amazon?',
              a: 'No.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for Amazon: the form eats the design',
      description:
        'ClarCV is not Amazon. Warehouse, corporate, or tech: a parseable PDF because ATS does not read Canva. Rewrite the file you already have.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Amazon runs highly automated processes. A creative PDF often dies in parsing or in a form that asks you to paste the CV in chunks. ClarCV is not Amazon, AWS, or Amazon Logistics. We do not have Jobscan against their ATS or a “2026 hack”. What does work in practice: selectable text, standard headings, keywords from the ad you can defend, and often an English version if the vacancy is in English.',
        },
        {
          type: 'p',
          text: 'Tech and corporate: [tech CV](/blog/cv-para-tecnologia) and [data analyst](/blog/cv-para-data-analyst). Warehouse / last mile: shifts and safety, closer to [hospitality](/blog/cv-para-hosteleria) in format (one page, availability). Language: [English or Spanish](/blog/cv-ingles-o-espanol). ClarCV: upload the PDF, structure, A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'What the form usually breaks' },
        {
          type: 'p',
          text: 'Columns, text boxes, phone icons, skill bars, a heavy photo. Pasting into Notepad must return email and dates. Guide: [Canva and ATS](/blog/cv-canva-y-ats). Do not put the Amazon logo in the header: you are not staff until hired, and it looks like spam.',
        },
        {
          type: 'ul',
          items: [
            'Title aligned to the ad (Warehouse Associate, SDM, SDE) without inflating level.',
            'Leadership principles: do not paste all 16 on the CV. One askable achievement beats jargon.',
            'Warehouse or product metrics only if they are yours and not confidential.',
            'One or two pages. A 15-year history gets compressed: [CV over 50](/blog/cv-mayores-de-50).',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Workday does not read your infographic',
          lead: 'Whoever the employer is: if the PDF does not paste in order, the form will fail. Regenerate one column of text. ClarCV does not apply to Amazon for you.',
          from: 'Creative CV',
          to: 'A PDF ATS can extract',
          button: 'Fix the parsing',
        },
        { type: 'h2', text: 'Warehouse vs corporate vs tech' },
        {
          type: 'p',
          text: 'Warehouse: shifts, forklift, safety, site town. Spain corporate: the job family (finance, HR, vendor). Tech: honest stack, no invented “Amazon intern”. Spend 20 minutes tailoring. Do not use the same PDF for picker and SDE.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does ClarCV guarantee passing Amazon ATS?',
              a: 'No. Nobody serious can. We improve clarity and parseable layout.',
            },
            {
              q: 'Should I write the CV in English?',
              a: 'If the posting is in English, yes. If it is Spanish for a local fulfilment centre, Spanish with the ad’s keywords.',
            },
            {
              q: 'Should I put “customer obsessed”?',
              a: 'Better a fact. Jargon copied from the corporate site does not replace an achievement.',
            },
            {
              q: 'Are you Amazon’s careers site?',
              a: 'No.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-sepe',
    date: '2026-08-22',
    category: 'career',
    related: ['europass-o-cv-moderno', 'que-no-poner-en-el-cv', 'enviar-cv-por-email'],
    es: {
      title: 'CV para el SEPE: qué archivo piden (y qué no es obligatorio)',
      description:
        'ClarCV no es el SEPE. Demanda de empleo, oficina y portal: Europass no siempre es obligatorio. Cómo dejar un PDF usable sin fingir ser la administración.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“CV para el SEPE” suele significar dos cosas: el currículum que subes o entregas como demandante de empleo, y el que envías a empresas que te llegan por la oficina. ClarCV no es el Servicio Público de Empleo Estatal, no tramita el paro ni la demanda, y no sustituye Cita Previa ni el sistema de la oficina. Las instrucciones oficiales (qué campos rellenar en la demanda, si piden un modelo concreto) las da el SEPE o tu comunidad. Aquí: qué archivo no romper y cuándo Europass no es obligatorio.',
        },
        {
          type: 'p',
          text: 'Europass: [cuándo dejar el formato oficial](/blog/europass-o-cv-moderno). Datos de más: [qué no poner](/blog/que-no-poner-en-el-cv). Si te mandan a una empresa por email: [enviar CV](/blog/enviar-cv-por-email). ClarCV genera un PDF A4 parseable para empresas y portales. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No genera el formulario interno de la oficina de empleo.',
        },
        { type: 'h2', text: 'Demanda de empleo ≠ el PDF que envías a una pyme' },
        {
          type: 'p',
          text: 'En la oficina o en la sede electrónica te pedirán datos de ocupación, formación y disponibilidad para intermediar. Eso puede vivir en su sistema, no en un Canva. El PDF que te piden “para enviar a empresas” debe poder abrirse: una o dos páginas, texto seleccionable, teléfono y email en claro. Un Europass de cinco páginas no ayuda a la pyme; un infográfico no ayuda al intermediador que copia y pega.',
        },
        {
          type: 'ul',
          items: [
            'Si un trámite pide modelo concreto o sede electrónica: ese canal gana. No lo sustituyas por ClarCV.',
            'Si te piden “un currículum en PDF” para ofertas: archivo parseable, no escaneo del DNI.',
            'Ocupación / CNO: en el sistema de demanda. En el PDF, el puesto en lenguaje de oferta.',
            'Foto: según te indiquen; no es universal. Guía de foto.',
            'Comunidades con servicio propio (SOC, SAE, etc.): mira su web; el nombre “SEPE” no cubre todo.',
          ],
        },
        { type: 'h2', text: '¿Europass obligatorio?' },
        {
          type: 'p',
          text: 'No en todos los trámites ni para todas las ofertas privadas. Algunas convocatorias europeas o de movilidad sí lo mencionan. Para una oferta de auxiliar o de hostelería que te pasa la oficina, un CV moderno de una página suele ser más usable. Si el funcionario o el anuncio exigen Europass, úsalo para ese envío y guarda el PDF corto para el resto.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El SEPE no es un generador de Canva — y ClarCV no es el SEPE',
          lead: 'Si te piden un PDF para empresas, regenera uno que se pueda copiar. Si el trámite es el de la demanda, sigue la sede oficial.',
          from: 'Escaneo o Europass eterno',
          to: 'PDF corto para empresas',
          button: 'Preparar el CV de envío',
        },
        { type: 'h2', text: 'Cómo rehacer el archivo que ya tienes' },
        {
          type: 'p',
          text: 'Quita DNI del PDF que vas a reenviar a empresas. Deja ocupación, formación y un teléfono que cojas. Sube el Word a ClarCV para plantilla ATS si el destino es una empresa con portal. Para la demanda electrónica, no intentes “mejorar” campos oficiales con una plantilla creativa.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿ClarCV inscribe la demanda de empleo?',
              a: 'No. Solo el documento PDF/Word de currículum para quien ya tiene un archivo.',
            },
            {
              q: '¿Tengo que usar Europass sí o sí?',
              a: 'No siempre. Lee el trámite o el anuncio. Para muchas pymes, un CV corto parseable es mejor.',
            },
            {
              q: '¿El mismo CV para el SEPE y para InfoJobs?',
              a: 'Los hechos sí. El formato: el portal privado agradece A4 parseable. El trámite público, lo que pida la sede.',
            },
            {
              q: '¿Sois un organismo público?',
              a: 'No. clarcv.com es un producto privado.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV for SEPE: which file they want (and what is not mandatory)',
      description:
        'ClarCV is not SEPE. Job-seeker registration, the office, and the portal: Europass is not always required. How to leave a usable PDF without pretending to be the administration.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“CV for SEPE” usually means two things: the résumé you upload or hand in as a registered job seeker, and the one you send to employers the office refers you to. ClarCV is not Spain’s public employment service, does not process unemployment benefit or registration, and does not replace Cita Previa or the office system. Official instructions (which fields to fill, whether they want a specific template) come from SEPE or your region. Here: which file not to break, and when Europass is not mandatory.',
        },
        {
          type: 'p',
          text: 'Europass: [when to leave the official format](/blog/europass-o-cv-moderno). Extra personal data: [what not to put](/blog/que-no-poner-en-el-cv). If they send you to a company by email: [emailing your CV](/blog/enviar-cv-por-email). ClarCV generates a parseable A4 PDF for companies and boards. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. It does not generate the employment office’s internal form.',
        },
        { type: 'h2', text: 'Job-seeker registration ≠ the PDF you send a small company' },
        {
          type: 'p',
          text: 'In the office or on the e-office they will want occupation, training, and availability for matching. That can live in their system, not in Canva. The PDF they ask “to send to employers” must open: one or two pages, selectable text, phone and email in the clear. A five-page Europass does not help an SME; an infographic does not help a caseworker who copy-pastes.',
        },
        {
          type: 'ul',
          items: [
            'If a procedure wants a specific form or e-office: that channel wins. Do not replace it with ClarCV.',
            'If they want “a CV as PDF” for vacancies: a parseable file, not a scan of your ID.',
            'Occupation / CNO: in the registration system. On the PDF, the job title in vacancy language.',
            'Photo: as they instruct; it is not universal. Photo guide.',
            'Regions with their own service (SOC, SAE, etc.): check their site; the name “SEPE” does not cover everything.',
          ],
        },
        { type: 'h2', text: 'Is Europass mandatory?' },
        {
          type: 'p',
          text: 'Not for every procedure or every private vacancy. Some European or mobility calls mention it. For an admin or hospitality offer the office forwards, a one-page modern CV is usually more usable. If the officer or the ad requires Europass, use it for that send and keep the short PDF for the rest.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'SEPE is not a Canva builder — and ClarCV is not SEPE',
          lead: 'If they want a PDF for employers, regenerate one that can be copied. If the procedure is registration, follow the official e-office.',
          from: 'Scan or endless Europass',
          to: 'A short PDF for employers',
          button: 'Prepare the sending CV',
        },
        { type: 'h2', text: 'How to rebuild the file you already have' },
        {
          type: 'p',
          text: 'Take national ID off the PDF you will forward to companies. Leave occupation, training, and a phone you answer. Upload the Word to ClarCV for an ATS template if the destination is a company with a portal. For electronic registration, do not “improve” official fields with a creative template.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does ClarCV register me as a job seeker?',
              a: 'No. Only the CV PDF/Word for someone who already has a file.',
            },
            {
              q: 'Must I use Europass?',
              a: 'Not always. Read the procedure or the ad. For many SMEs, a short parseable CV is better.',
            },
            {
              q: 'Same CV for SEPE and InfoJobs?',
              a: 'The facts yes. Format: the private board likes parseable A4. The public procedure, whatever the e-office asks.',
            },
            {
              q: 'Are you a public body?',
              a: 'No. clarcv.com is a private product.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
